import { useState, useEffect, useCallback } from 'react';
import { StorageHookOptions, StorageHookReturn } from '@/types/storage';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage';

/**
 * Хук для работы с localStorage
 */
export const useLocalStorage = <T = any>(
  key: string,
  defaultValue: T
): StorageHookReturn<T> => {
  return useStorage<T>({ key, defaultValue, storage: 'localStorage' });
};

/**
 * Хук для работы с sessionStorage
 */
export const useSessionStorage = <T = any>(
  key: string,
  defaultValue: T
): StorageHookReturn<T> => {
  return useStorage<T>({ key, defaultValue, storage: 'sessionStorage' });
};

/**
 * Универсальный хук для работы с storage
 */
export const useStorage = <T = any>(
  options: StorageHookOptions<T>
): StorageHookReturn<T> => {
  const {
    key,
    defaultValue,
    storage = 'localStorage',
    serialize = JSON.stringify,
    deserialize = JSON.parse
  } = options;

  const [value, setValueState] = useState<T>(defaultValue);
  const [isLoaded, setIsLoaded] = useState(false);

  // Загрузка значения при инициализации
  useEffect(() => {
    try {
      const storedValue = getStorageItem<T>(key, {
        storage,
        defaultValue,
        deserialize
      });
      
      setValueState(storedValue ?? defaultValue);
    } catch (error) {
      console.error(`Error loading storage value for key "${key}":`, error);
      setValueState(defaultValue);
    } finally {
      setIsLoaded(true);
    }
  }, [key, storage]);

  // Сохранение значения при изменении
  const setValue = useCallback((newValue: T | ((prev: T) => T)) => {
    try {
      const valueToStore = typeof newValue === 'function' 
        ? (newValue as (prev: T) => T)(value)
        : newValue;
      
      setValueState(valueToStore);
      
      const success = setStorageItem(key, valueToStore, {
        storage,
        serialize
      });
      
      if (!success) {
        console.error(`Failed to save value for key "${key}"`);
      }
    } catch (error) {
      console.error(`Error setting storage value for key "${key}":`, error);
    }
  }, [key, storage, serialize, value]);

  // Удаление значения
  const removeValue = useCallback(() => {
    try {
      setValueState(defaultValue);
      removeStorageItem(key, storage);
    } catch (error) {
      console.error(`Error removing storage value for key "${key}":`, error);
    }
  }, [key, storage, defaultValue]);

  return {
    value,
    setValue,
    removeValue,
    isLoaded
  };
};

/**
 * Хук для работы с кэшем с TTL (time to live)
 */
export const useCache = <T = any>(
  key: string,
  defaultValue: T,
  ttl: number = 5 * 60 * 1000 // 5 минут по умолчанию
) => {
  const cacheKey = `cache:${key}`;
  
  const { value: cacheData, setValue: setCacheData } = useLocalStorage(cacheKey, {
    data: defaultValue,
    timestamp: Date.now(),
    ttl
  });

  const isExpired = Date.now() - cacheData.timestamp > cacheData.ttl;
  const currentValue = isExpired ? defaultValue : cacheData.data;

  const setValue = useCallback((newValue: T) => {
    setCacheData({
      data: newValue,
      timestamp: Date.now(),
      ttl
    });
  }, [setCacheData, ttl]);

  const clearCache = useCallback(() => {
    setCacheData({
      data: defaultValue,
      timestamp: 0,
      ttl
    });
  }, [setCacheData, defaultValue, ttl]);

  return {
    value: currentValue,
    setValue,
    clearCache,
    isExpired,
    isLoaded: true
  };
};

/**
 * Хук для работы с формой и автосохранением
 */
export const useFormStorage = <T extends Record<string, any>>(
  key: string,
  defaultValue: T,
  autoSave: boolean = true,
  debounceMs: number = 500
) => {
  const { value, setValue, isLoaded } = useLocalStorage(key, defaultValue);
  const [isDirty, setIsDirty] = useState(false);

  // Debounce для автосохранения
  useEffect(() => {
    if (!autoSave || !isDirty) return;

    const timer = setTimeout(() => {
      setValue(value);
      setIsDirty(false);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [value, autoSave, isDirty, debounceMs, setValue]);

  const updateField = useCallback((field: keyof T, fieldValue: any) => {
    setValue(prev => ({
      ...prev,
      [field]: fieldValue
    }));
    setIsDirty(true);
  }, [setValue]);

  const updateFields = useCallback((fields: Partial<T>) => {
    setValue(prev => ({
      ...prev,
      ...fields
    }));
    setIsDirty(true);
  }, [setValue]);

  const resetForm = useCallback(() => {
    setValue(defaultValue);
    setIsDirty(false);
  }, [setValue, defaultValue]);

  return {
    formData: value,
    updateField,
    updateFields,
    resetForm,
    isDirty,
    isLoaded
  };
};
